<template>
  <div
    class="flex flex-row gap-3 items-center justify-center flex-wrap md:gap-6 md:flex-nowrap xl:justify-end"
  >
    <!--DASHBOARD LINK-->
    <router-link
      id="dashboard-link"
      activeClass="!text-fg-primary"
      class="text-fg-link-primary"
      :to="{ name: PERFORMANCE_ROUTE_NAMES.DASHBOARD }"
    >
      Dashboard
    </router-link>

    <UpdateIndicator
      v-for="link in _.filter(perEditPageLinks, { displayInDesktop: true })"
      :key="link.routeName"
      class="perf-page-link perf-page-link-desktop hidden md:block"
      :class="{ 'md:!hidden': link.hidden || displayEditLink }"
      :status="link.isUpdated ? 'updated' : 'none'"
      :id="`${link.routeName}-link-desktop`"
    >
      <router-link
        activeClass="!text-fg-primary"
        class="text-fg-link-primary"
        :to="{ name: link.routeName }"
      >
        {{ link.label }}
      </router-link>
    </UpdateIndicator>

    <!--LINK AND ACTION MENU-->
    <MenuDisplay class="relative">
      <template #trigger>
        <div class="flex items-center justify-center w-6 h-6 text-fg-link-primary">
          <ChevronDownIcon class="w-6 h-6" />
        </div>
      </template>
      <template #menu>
        <div
          class="absolute left-0 top-10 z-20 bg-surface-primary px-4 py-4 shadow-podHover rounded w-52 flex-col flex gap-2"
        >
          <UpdateIndicator
            v-for="link in perEditPageLinks"
            :key="link.routeName"
            class="perf-page-link"
            :class="{
              hidden: link.hidden,
              'md:hidden': link.displayInDesktop,
              'md:!block': displayEditLink,
            }"
            :status="link.isUpdated ? 'updated' : 'none'"
            :id="`${link.routeName}-link`"
          >
            <router-link
              activeClass="!text-fg-primary"
              class="text-fg-link-primary"
              :to="{ name: link.routeName }"
            >
              {{ link.label }}
            </router-link>
          </UpdateIndicator>
          <hr class="w-full border border-linear-strong/50" />
          <PerformanceActions class="flex flex-col gap-2" />
        </div>
      </template>
    </MenuDisplay>

    <!--RESERVATIONS LINK-->
    <span class="text-linear-strong block">|</span>
    <router-link
      :id="`${PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST}-link`"
      activeClass="!text-fg-primary"
      class="perf-page-link text-fg-link-primary"
      :to="{ name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST }"
    >
      <span class="font-semibold">{{ dashboardInformation.reservations.length }}</span
      >Res&nbsp;&nbsp;<span class="font-semibold">{{ dashboardInformation.numberOfGuests }}</span
      >Guests&nbsp;&nbsp;<span class="font-semibold">{{
        dashboardInformation.numberOfWalkIns
      }}</span
      >W
    </router-link>
  </div>
</template>

<script lang="ts" setup>
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core/index'
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import ChevronDownIcon from '@/assets/chevronDown.svg?component'
import MenuDisplay from '@/common/components/atoms/MenuDisplay.vue'
import UpdateIndicator from '@/common/components/atoms/UpdateIndicator.vue'
import PerformanceActions from '@/modules/performance/components/molecules/PerformanceActions.vue'
import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import { usePerformanceUpdateStore } from '@/modules/performance/stores/PerformanceUpdateStore'
import { useAddOnStore } from '@/stores/addons'

type PerformanceEditPageLink = {
  routeName: string
  label: string
  isUpdated: boolean
  hidden: boolean
  displayInDesktop: boolean
}

const { dashboardInformation } = storeToRefs(usePerformanceReservationStore())
const {
  isSchedulePageUpdated,
  isPerformanceTablePageUpdated,
  isSeatingTimePageUpdated,
  isAddOnsPageUpdated,
} = storeToRefs(usePerformanceUpdateStore())

const addOnStore = useAddOnStore()
const { addOns } = storeToRefs(addOnStore)
onMounted(async () => {
  await addOnStore.fetchAll()
})

const perEditPageLinks = computed<PerformanceEditPageLink[]>(() => {
  return [
    {
      routeName: PERFORMANCE_ROUTE_NAMES.SCHEDULE,
      label: 'Schedule',
      isUpdated: isSchedulePageUpdated.value,
      hidden: false,
      displayInDesktop: true,
    },
    {
      routeName: PERFORMANCE_ROUTE_NAMES.TABLES,
      label: 'Tables',
      isUpdated: isPerformanceTablePageUpdated.value,
      hidden: false,
      displayInDesktop: true,
    },
    {
      routeName: PERFORMANCE_ROUTE_NAMES.SEATING_TIMES,
      label: 'Seating Times',
      isUpdated: isSeatingTimePageUpdated.value,
      hidden: false,
      displayInDesktop: false,
    },
    {
      routeName: PERFORMANCE_ROUTE_NAMES.ADD_ONS,
      label: 'Add-ons',
      isUpdated: isAddOnsPageUpdated.value,
      hidden: addOns.value.length === 0,
      displayInDesktop: false,
    },
    {
      routeName: PERFORMANCE_ROUTE_NAMES.PRICING_TIERS,
      label: 'Pricing Tiers',
      isUpdated: false,
      hidden: false,
      displayInDesktop: false,
    },
  ]
})

const route = useRoute()

const breakpoints = useBreakpoints(breakpointsTailwind)
const isDesktop = breakpoints.isGreaterOrEqual('xl')
const displayEditLink = computed<boolean>(() => route.path.includes('reservations/') && isDesktop)
</script>
